import { create } from 'zustand';
import { AuthStore } from './authStore';
import { persist } from 'zustand/middleware';
import { useChatStore } from '../chat/use-chat-store';
import { useModelStore } from '../chat/use-model-store';
import { useCommonStore } from '../common/useCommonStore';
import posthog from 'analytics/posthogClient';
import { socketManager } from 'utils/socket-manager';

/**
 * Only these fields reach `localStorage` ('auth-storage'). Access, refresh and
 * ID tokens stay in memory: the httpOnly `rt_` cookie brings a fresh access
 * token back after a reload (see `waitForSessionRestore` in auth-service).
 */
type PersistedAuthState = Pick<
  AuthStore,
  'isAuthenticated' | 'isMfaEnabled' | 'selectedOrgId' | 'isOIDCLogin'
>;

const PERSISTED_AUTH_KEYS: (keyof PersistedAuthState)[] = [
  'isAuthenticated',
  'isMfaEnabled',
  'selectedOrgId',
  'isOIDCLogin',
];

// Version 0 (before this change) also stored accessToken and refreshToken.
export const AUTH_STORAGE_VERSION = 1;

export const pickPersistedAuthState = (state: unknown): Partial<PersistedAuthState> => {
  if (!state || typeof state !== 'object') return {};
  const source = state as Record<string, unknown>;
  const picked: Record<string, unknown> = {};
  PERSISTED_AUTH_KEYS.forEach((key) => {
    if (key in source) picked[key] = source[key];
  });
  return picked as Partial<PersistedAuthState>;
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      isMfaEnabled: false,
      accessToken: null,
      refreshToken: null,
      selectedOrgId: null,
      isOIDCLogin: false,
      needsSessionRestore: false,
      login: (accessToken, refreshToken) =>
        set({
          isAuthenticated: true,
          isMfaEnabled: true,
          accessToken,
          refreshToken,
          needsSessionRestore: false,
        }),
      // A cookie-only sign-in (IdP callback): start without any token, so nothing
      // left from an earlier session (a late refresh, say) is ever sent.
      setAuthenticated: () =>
        set({
          isAuthenticated: true,
          isMfaEnabled: true,
          accessToken: null,
          refreshToken: null,
          needsSessionRestore: false,
        }),
      // Does not clear needsSessionRestore: only the boot restore does, once it has
      // also checked the organization (see waitForSessionRestore).
      setAccessToken: (accessToken) =>
        set({
          accessToken,
        }),
      setRefreshToken: (refreshToken) =>
        set({
          refreshToken,
        }),
      setSelectedOrgId: (selectedOrgId) =>
        set({
          selectedOrgId,
        }),
      setIsOIDCLogin: (isOIDCLogin) =>
        set({
          isOIDCLogin,
        }),
      setSessionRestored: () =>
        set({
          needsSessionRestore: false,
        }),
      logout: () => {
        // Clear auth state
        try {
          posthog.reset();
        } catch (error) {
          console.error('Error resetting PostHog: ', error);
        }
        socketManager.cleanup();
        useChatStore.getState().reset();
        useModelStore.getState().reset();
        useCommonStore.getState().reset();

        set({
          isAuthenticated: false,
          isMfaEnabled: false,
          accessToken: null,
          refreshToken: null,
          selectedOrgId: null,
          isOIDCLogin: false,
          needsSessionRestore: false,
        });

        // Clear subscription state (import done dynamically to avoid circular deps)
        import('../subscription').then(({ useSubscriptionStore }) => {
          useSubscriptionStore.getState().clearSubscription();
        });
      },
    }),
    {
      name: 'auth-storage',
      version: AUTH_STORAGE_VERSION,
      partialize: (state) => pickPersistedAuthState(state),
      // An old value is rewritten without its tokens on first load. The signed-in
      // flag stays, so the user is not logged out: the boot refresh takes over.
      migrate: (persistedState) => pickPersistedAuthState(persistedState),
      // Never take tokens from storage, whatever its version. On a rehydrate (another
      // tab changed 'auth-storage') this tab keeps its in-memory tokens only while
      // storage still describes the same signed-in session; after a logout or a new
      // session there, a previous user's bearer must never be sent from here.
      // A stored signed-in flag without a token asks for one cookie refresh first,
      // except for IdP sessions (isOIDCLogin): they never had store tokens at sign-in
      // and run on the session cookie, as before this change.
      merge: (persistedState, currentState) => {
        const persisted = pickPersistedAuthState(persistedState);
        const isSignedIn = Boolean(persisted.isAuthenticated);
        const isSameSession =
          isSignedIn &&
          currentState.isAuthenticated &&
          (persisted.selectedOrgId ?? null) === (currentState.selectedOrgId ?? null);
        const accessToken = isSameSession ? currentState.accessToken : null;
        const refreshToken = isSameSession ? currentState.refreshToken : null;
        return {
          ...currentState,
          ...persisted,
          accessToken,
          refreshToken,
          needsSessionRestore: isSignedIn && !persisted.isOIDCLogin && !accessToken,
        };
      },
    }
  )
);
